import { useMemo, useState } from "react";
import { Slider } from "./ui/slider";
import { Gavel, WarningCircle } from "@phosphor-icons/react";

function eur(n) {
    if (n == null || Number.isNaN(Number(n))) return "—";
    return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n));
}

const MIN_PROFIT = 15000;

export const BidSimulator = ({ inv, auction }) => {
    const sc = inv?.scenarios?.realistic || {};
    const model = useMemo(() => {
        const A = Number(sc.acquisition_price);
        const TC = Number(sc.total_costs);
        const S = Number(sc.estimated_sale_price ?? sc.exit_value);
        if (!A || !TC || !S) return null;
        const deductible = (sc.purchase_costs || 0) + (sc.technical_costs || 0) + (sc.renovation_costs || 0);
        const breakdown = inv.cost_breakdown || [];
        const regItem = breakdown.find((b) => (b.label || "").toLowerCase().includes("registro"));
        const reg = Number(regItem?.amount_eur) || 0;
        // Registro proporzionale (9% del prezzo) o fisso (prezzo-valore / IVA)
        const proportional = reg > 0 && Math.abs(reg - 0.09 * A) <= 0.2 * (0.09 * A);
        const at = (P) => {
            const dReg = proportional ? Math.max(1000, 0.09 * P) - reg : 0;
            const tc = TC + dReg;
            const taxable = S - P - (deductible + dReg);
            const plusv = Math.round(Math.max(0, taxable) * 0.26);
            const net = Math.round(S - P - tc - plusv);
            const invested = P + tc;
            const roi = invested > 0 ? (net / invested) * 100 : 0;
            const ann = sc.holding_months ? (roi * 12) / sc.holding_months : null;
            return { net, plusv, roi, ann, tc };
        };
        // net(P) è strettamente decrescente → ricerca binaria del prezzo target
        const solve = (target) => {
            let lo = 0, hi = S * 1.5;
            for (let i = 0; i < 60; i++) {
                const mid = (lo + hi) / 2;
                if (at(mid).net > target) lo = mid; else hi = mid;
            }
            return Math.round(lo / 100) * 100;
        };
        const breakeven = solve(0);
        const maxSafeBid = solve(MIN_PROFIT);
        const base = Number(auction?.base_price) || null;
        const minOffer = Number(auction?.min_offer) || null;
        const lo = Math.max(1000, Math.round(Math.min(minOffer || base || A * 0.8, A * 0.8) / 500) * 500);
        const hi = Math.max(Math.round((breakeven * 1.15) / 500) * 500, A);
        return { A, S, at, breakeven, maxSafeBid, lo, hi, base, minOffer };
    }, [inv, auction]); // eslint-disable-line react-hooks/exhaustive-deps

    const [bid, setBid] = useState(null);
    if (!model) return null;
    const P = bid ?? model.A;
    const r = model.at(P);
    const zone = r.net >= MIN_PROFIT ? "ok" : r.net > 0 ? "thin" : "dead";
    const zoneCfg = {
        ok: { color: "var(--acc-green)", label: "AFFARE SOLIDO", bg: "bg-emerald-50 border-emerald-300" },
        thin: { color: "#D97706", label: "MARGINE SOTTILE", bg: "bg-amber-50 border-amber-300" },
        dead: { color: "var(--acc-red)", label: "L'AFFARE MUORE", bg: "bg-red-50 border-red-400" },
    }[zone];
    const pct = (v) => Math.min(100, Math.max(0, ((v - model.lo) / (model.hi - model.lo)) * 100));
    const labelPct = (v) => Math.min(92, Math.max(6, pct(v)));

    return (
        <div className="bg-white border-2 border-gray-900 mb-6 fade-up" data-testid="bid-simulator">
            <div className="border-b-2 border-gray-900 px-6 py-4 flex items-center justify-between flex-wrap gap-2">
                <div>
                    <div className="overline mb-1">Da portare in asta</div>
                    <div className="font-display-black text-2xl flex items-center gap-2">
                        <Gavel size={22} weight="bold" /> Simulatore di Rilancio
                    </div>
                </div>
                <div className="text-right">
                    <div className="overline text-red-600">Sopra questo prezzo l&apos;affare muore</div>
                    <div className="font-display-black text-2xl text-red-600" data-testid="breakeven-price">
                        {eur(model.breakeven)}
                    </div>
                </div>
            </div>

            <div className="px-6 py-5">
                <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
                    <div>
                        <div className="overline mb-1">Se ti aggiudichi a</div>
                        <div className="font-display-black text-4xl" data-testid="simulated-bid">{eur(P)}</div>
                    </div>
                    <div className={`border-2 px-4 py-2 ${zoneCfg.bg}`} data-testid="bid-verdict">
                        <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: zoneCfg.color }}>
                            {zoneCfg.label}
                        </div>
                        <div className="font-display-black text-2xl" style={{ color: zoneCfg.color }} data-testid="simulated-net-profit">
                            {r.net > 0 ? "+" : ""}{eur(r.net)}
                        </div>
                        <div className="text-[10px] text-gray-500 uppercase tracking-wider">
                            in tasca post-tasse{r.ann != null ? ` · ${r.roi.toFixed(1)}% ROI · ${r.ann.toFixed(1)}%/anno` : ` · ${r.roi.toFixed(1)}% ROI`}
                        </div>
                    </div>
                </div>

                {/* Zone bar + markers */}
                <div className="relative h-3 rounded-full overflow-hidden mb-1" data-testid="bid-zone-bar">
                    <div className="absolute inset-0 flex">
                        <div className="h-full bg-emerald-400" style={{ width: `${pct(model.maxSafeBid)}%` }} />
                        <div className="h-full bg-amber-400" style={{ width: `${pct(model.breakeven) - pct(model.maxSafeBid)}%` }} />
                        <div className="h-full bg-red-400 flex-1" />
                    </div>
                    <div className="absolute top-0 h-full w-0.5 bg-black" style={{ left: `${pct(P)}%` }} />
                </div>
                <div className="relative h-4 text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-3">
                    {model.base && model.base >= model.lo && model.base <= model.hi && (
                        <span className="absolute -translate-x-1/2" style={{ left: `${labelPct(model.base)}%` }}>Base {eur(model.base)}</span>
                    )}
                    <span className="absolute -translate-x-1/2 text-red-600" style={{ left: `${labelPct(model.breakeven)}%` }}>☠ {eur(model.breakeven)}</span>
                </div>

                <Slider
                    value={[P]}
                    min={model.lo}
                    max={model.hi}
                    step={500}
                    onValueChange={(v) => setBid(v[0])}
                    data-testid="bid-slider"
                    className="mb-5"
                />

                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 border border-gray-200 text-center">
                    <div className="bg-white p-3" data-testid="max-safe-bid">
                        <div className="text-[10px] uppercase tracking-wider text-gray-500">Offerta max consigliata</div>
                        <div className="font-display-black text-lg text-emerald-700">{eur(model.maxSafeBid)}</div>
                        <div className="text-[10px] text-gray-400">profitto ≥ {eur(MIN_PROFIT)}</div>
                    </div>
                    <div className="bg-white p-3">
                        <div className="text-[10px] uppercase tracking-wider text-gray-500">Punto di pareggio</div>
                        <div className="font-display-black text-lg text-red-600">{eur(model.breakeven)}</div>
                        <div className="text-[10px] text-gray-400">oltre = perdita</div>
                    </div>
                    <div className="bg-white p-3">
                        <div className="text-[10px] uppercase tracking-wider text-gray-500">Costi totali a {eur(P)}</div>
                        <div className="font-display-black text-lg">{eur(r.tc)}</div>
                        <div className="text-[10px] text-gray-400">incl. imposte d&apos;acquisto</div>
                    </div>
                    <div className="bg-white p-3">
                        <div className="text-[10px] uppercase tracking-wider text-gray-500">Plusvalenza (26%)</div>
                        <div className="font-display-black text-lg">{eur(r.plusv)}</div>
                        <div className="text-[10px] text-gray-400">già sottratta dal netto</div>
                    </div>
                </div>

                <p className="text-[11px] text-gray-500 mt-3 flex items-start gap-1.5">
                    <WarningCircle size={13} className="shrink-0 mt-0.5" />
                    Simulazione sullo scenario realistico (vendita a {eur(model.S)}). Stima indicativa, non è consulenza finanziaria:
                    porta in asta l&apos;offerta massima e non superarla mai.
                </p>
            </div>
        </div>
    );
};

export default BidSimulator;
